import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { FaArrowRight, FaCalendarAlt, FaClock, FaUser } from "react-icons/fa";

const THEME = {
  shell: "bg-gradient-to-br from-[#180303] via-[#2a0707] to-[#0f0202]",
  frame: "shadow-[0_0_90px_rgba(255,59,59,0.18)]",
  accentBar: "bg-[linear-gradient(90deg,#ff1f1f_0%,#ff3b3b_45%,#b30000_100%)]",
  panel: "bg-[#0f0c18]/88",
};

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
}

export default function Blogs() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blogs • Light Idea Productions";
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .order("published_date", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Failed to fetch blog posts:", error.message);
        setPosts([]);
        setLoading(false);
        return;
      }

      setPosts(data || []);
      setLoading(false);
    };

    fetchPosts();
  }, []);

  return (
    <div className={`min-h-screen ${THEME.shell} text-white`}>
      <div className="mx-auto w-full max-w-[1400px] px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <div className="mb-8 overflow-hidden rounded-[30px] border border-white/10 bg-black/35">
          <div className={`h-1.5 w-full ${THEME.accentBar}`} />
          <div className="px-5 py-6 sm:px-8 sm:py-8 lg:px-10">
            <div className="mb-2 text-[11px] uppercase tracking-[0.28em] text-white/45">
              Light Idea Productions
            </div>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              Latest Blog Posts
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-white/72 sm:text-base">
              Product updates, platform changes, and official posts from Light
              Idea Productions.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="rounded-[24px] border border-white/10 bg-black/30 px-5 py-8 text-white/70">
            Loading blog posts...
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-[24px] border border-white/10 bg-black/30 px-5 py-8 text-white/70">
            No blog posts have been published yet.
          </div>
        ) : (
          <div className="grid items-stretch gap-5 md:grid-cols-2 xl:grid-cols-3">
            {posts.map((post) => (
              <button
                key={post.id}
                type="button"
                onClick={() => navigate(`/blogs/${post.slug}`)}
                className={`flex h-full flex-col overflow-hidden rounded-[24px] border border-white/10 ${THEME.panel} p-5 text-left transition hover:scale-[1.01] hover:border-white/20 ${THEME.frame}`}
              >
                <div className="mb-3 inline-flex w-fit rounded-full bg-white/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-white/75">
                  {post.category}
                </div>

                <div className="min-h-[84px]">
                  <h2 className="line-clamp-3 text-2xl font-semibold tracking-tight text-white">
                    {post.title}
                  </h2>
                </div>

                <div className="mt-3 min-h-[96px]">
                  <p className="line-clamp-3 text-sm leading-7 text-white/72">
                    {post.overview || post.summary || " "}
                  </p>
                </div>

                <div className="mt-auto pt-5">
                  <div className="flex flex-wrap items-center gap-4 text-xs text-white/58 sm:text-sm">
                    <span className="inline-flex items-center gap-2">
                      <FaUser className="h-3 w-3" />
                      {post.author_name}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaCalendarAlt className="h-3 w-3" />
                      {formatDate(post.published_date)}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaClock className="h-3 w-3" />
                      {post.read_time_minutes} min read
                    </span>
                  </div>

                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white">
                    Read post
                    <FaArrowRight className="h-3 w-3" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
